"use client";

import { useState, useMemo, useCallback } from "react";
import { Check, Loader2, ShoppingBag, Minus, Plus, Trash } from "lucide-react";
import { MainShopProduct } from "@/core/models/shop-product-model";
import { useCartStore } from "@/core/hooks/cart/useCartStore";
import { useAuth } from "@/contexts/authContext";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
import CustomButton from "../../components/CustomButton";

interface CollectionCartSelectorProps {
    products: MainShopProduct[];
    settings?: any;
}

export function CollectionCartSelector({ products, settings }: CollectionCartSelectorProps) {
    const ProductCard = loadThemeComponent("ProductCard");
    const [selections, setSelections] = useState<Map<string, number>>(new Map());
    const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
    const { addToCart, isCartLoading } = useCartStore();
    const { state: { isAuthenticated } } = useAuth();
    const currentCart = useCartStore.getState().cart;
    console.log(products);


    const currentItem = currentCart?.items?.find(
        //@ts-ignore
        item => item.product_id === products.id
    );
    const currentQuantity = currentItem?.quantity ?? 0;


    const inStockProducts = useMemo(
        () => products.filter((p) => p.inventory && p.inventory > 0),
        [products]
    );

    const selectedCount = selections.size;
    const allSelected = selectedCount === inStockProducts.length && inStockProducts.length > 0;

    const { totalPrice, totalItems } = useMemo(() => {
        let price = 0, items = 0;
        selections.forEach((qty, id) => {
            const p = products.find((x) => x.id === id);
            if (p) {
                price += (p.price_withDiscount || p.price || 0) * qty;
                items += qty;
            }
        });
        return { totalPrice: price, totalItems: items };
    }, [selections, products]);

    const toggleSelect = useCallback((id: string) => {
        setSelections((prev) => {
            const next = new Map(prev);

            if (next.has(id)) {
                next.delete(id);
            } else {
                next.set(id, 1);
            }

            return next;
        });
    }, []);

    const setQuantity = useCallback((id: string, qty: number) => {
        if (qty < 1) return;
        setSelections((prev) => new Map(prev).set(id, qty));
    }, []);

    const selectAll = useCallback(() => {
        if (allSelected) {
            setSelections(new Map());
        } else {
            const next = new Map<string, number>();
            inStockProducts.forEach((p) => next.set(p.id, 1));
            setSelections(next);
        }
    }, [allSelected, inStockProducts]);

    const handleAddToCart = async () => {
        if (selections.size === 0) return;
        setStatus("loading");
        try {
            for (const [id, qty] of selections) {
                const product = products.find((p) => p.id === id);
                if (product) await addToCart(product, qty, isAuthenticated);
            }
            setStatus("success");
            setTimeout(() => {
                setStatus("idle");
                setSelections(new Map());
            }, 2000);
        } catch {
            setStatus("idle");
        }
    };

    const isDisabled = status !== "idle" || isCartLoading;

    return (
        <div>
            {/* Toolbar */}
            <div className="sticky top-0 z-20 bg-neutral-50 border border-neutral-200 p-4 mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <div className={`w-5 h-5 border-2 flex items-center justify-center transition-colors ${allSelected ? "bg-neutral-900 border-neutral-900" : "border-neutral-300 hover:border-neutral-400"}`}>
                            {allSelected && <Check size={12} className="text-white" />}
                        </div>
                        <input type="checkbox" checked={allSelected} onChange={selectAll} className="sr-only" />
                        <span className="text-sm text-neutral-700">انتخاب همه</span>
                        <span className="text-xs text-neutral-400">({inStockProducts.length} موجود)</span>
                    </label>

                    {selectedCount > 0 && (
                        <span className="text-sm text-neutral-900 font-medium">
                            {selectedCount} انتخاب شده
                        </span>
                    )}
                </div>

                {selectedCount > 0 && (
                    <div className="hidden md:flex items-center gap-6">
                        <div className="flex flex-col items-end">
                            <span className="text-xs text-neutral-500">{totalItems} عدد</span>
                            <span className="text-base font-medium text-neutral-900">
                                {totalPrice.toLocaleString("fa-IR")} تومان
                            </span>
                        </div>
                        <CustomButton onClick={handleAddToCart} disabled={isDisabled} variant="secondary" className="gap-2 px-6">
                            {status === "loading" ? (
                                <><Loader2 size={16} className="animate-spin" /> در حال افزودن</>
                            ) : status === "success" ? (
                                <><Check size={16} /> اضافه شد</>
                            ) : (
                                <><ShoppingBag size={16} /> افزودن به سبد</>
                            )}
                        </CustomButton>
                    </div>
                )}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {products.map((product, index) => {
                    const isSelected = selections.has(product.id);
                    const qty = selections.get(product.id) || 1;
                    const inStock = product.inventory && product.inventory > 0;


                    return (
                        <div
                            key={product.id}
                            className={`relative group transition-all duration-200 ${isSelected
                                ? "ring-2 ring-neutral-900 ring-offset-0"
                                : "hover:ring-1 hover:ring-neutral-200"
                                }`}
                        >
                            {/* Checkbox - only for in stock */}
                            {inStock ? (
                                <button
                                    type="button"
                                    onClick={() => toggleSelect(product.id)}
                                    className={`absolute top-3 left-3 ltr:right-3 ltr:left-auto z-10 w-6 h-6 border-2 flex items-center justify-center transition-all ${isSelected
                                        ? "bg-neutral-900 border-neutral-900"
                                        : "bg-white/80 border-neutral-300 hover:border-neutral-400"
                                        }`}
                                >
                                    {isSelected && <Check size={14} className="text-white" />}
                                </button>
                            ) : ""}

                            <ProductCard product={product} settings={settings} priority={index < 4} />

                            {/* Quantity Controls */}
                            {isSelected && (
                                <div className="absolute inset-0 bg-primary-0/90 text-white flex flex-col items-center justify-center gap-6">
                                    <div className="flex items-center justify-center gap-3">
                                        <button
                                            type="button"
                                            disabled={isCartLoading || currentQuantity >= product.inventory}
                                            onClick={() => qty <= 1 ? toggleSelect(product.id) : setQuantity(product.id, qty - 1)}
                                            className="p-2 rounded-full hover:hover:bg-primary-200 text-primary-900 transition-colors"
                                        >
                                            {qty <= 1 ? <Trash size={18} /> : <Minus size={18} />}
                                        </button>
                                        <span className="font-medium min-w-12 text-center text-lg text-primary-900">{qty}</span>
                                        <button
                                            type="button"
                                            onClick={() => setQuantity(product.id, qty + 1)}
                                            disabled={product.inventory ? qty >= product.inventory : false}
                                            className="p-2 rounded-full hover:bg-gray-200 text-primary-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Plus size={18} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Mobile Bar */}
            {selectedCount > 0 && (
                <div className="fixed bottom-0 inset-x-0 md:hidden bg-white border-t border-neutral-200 p-4 z-50">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex flex-col">
                            <span className="text-xs text-neutral-500">{selectedCount} محصول ({totalItems} عدد)</span>
                            <span className="text-sm font-medium text-neutral-900">
                                {totalPrice.toLocaleString("fa-IR")} تومان
                            </span>
                        </div>
                        <CustomButton onClick={handleAddToCart} disabled={isDisabled} variant="secondary" className="flex-1 gap-2">
                            {status === "loading" ? (
                                <><Loader2 size={16} className="animate-spin" /> افزودن...</>
                            ) : status === "success" ? (
                                <><Check size={16} /> اضافه شد</>
                            ) : (
                                <><ShoppingBag size={16} /> افزودن به سبد</>
                            )}
                        </CustomButton>
                    </div>
                </div>
            )}
        </div>
    );
}
